<template>
	<div v-if="pages && pages.length > 1" class="my-6 md:my-8">
		<ul class="flex items-center justify-center">
			<li v-if="pageNumber > 1">
				<button type="button" @click="onPrevClick" :class="buttonClasses">
					<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6">
						<use :href="iconHref('chevronLeft')" />
					</svg>
					<span class="sr-only">{{ previousLabel }}</span>
				</button>
			</li>
			<li v-for="page in pages" :key="'pagination-page-' + page">
				<button
					type="button"
					@click="onPageClick(page)"
					:class="['p-2', { 'font-bold hover:underline': pageNumber !== page }]"
					:disabled="pageNumber === page"
					:aria-label="`Go to page ${page} of ${pageCount}`"
				>
					{{ page }}
				</button>
			</li>
			<li v-if="pageNumber < pageCount">
				<button type="button" @click="onNextClick" :class="buttonClasses">
					<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6">
						<use :href="iconHref('chevronRight')" />
					</svg>
					<span class="sr-only">{{ nextLabel }}</span>
				</button>
			</li>
		</ul>
	</div>
</template>

<script>
import Base from "./../base"
import { cloneObject } from "../../../../utils"
import EventBus from "./../../../../bus"
import Events from "../../../news-listing/events"
import EventsSearch from "../../../search/events"
import UrlUpdater from "../../../news-listing/utils/url-updater"

import IconChevronLeft from "../../../../../assets/icons/chevron-left.svg"
import IconChevronRight from "../../../../../assets/icons/chevron-right.svg"

const iconMap = {
	chevronLeft: IconChevronLeft,
	chevronRight: IconChevronRight,
}

export default {
	name: "IntProductListingPagination",

	mixins: [Base],

	props: {
		initialPageNumber: { type: Number, default: 1 },
		initialItemCount: { type: Number, default: 0 },
		initialPageSize: { type: Number, default: 8 },
		previousLabel: { type: String, default: "Previous page" },
		nextLabel: { type: String, default: "Next page" },
		selectionCriteria: { type: String, default: "" },
		initialFilters: { type: Array, default: [] },
	},

	data() {
		return {
			pageNumber: this.initialPageNumber,
			itemCount: this.initialItemCount,
			pageSize: this.initialPageSize,
			pageCount: 0,
			buttonClasses:
				"p-1 border-[1px] border-gray-200 rounded-full hover:bg-blue hover:border-blue hover:fill-white",
			filters: this.initialFilters.filter((filter) => filter.display === true) || [],
			selectedSearchFilter: [],
			selectedCategory: [],
			selectedRange: [],
			selectedItem: [],
			selectedSize: [],
		}
	},

	computed: {
		pages() {
			if (this.itemCount === 0) return []

			let page = this.pageNumber
			this.pageCount = Math.ceil(this.itemCount / this.pageSize)
			if (this.pageCount === 0) return []

			let start = page - 2
			let end = page + 2

			if (end > this.pageCount) {
				start -= end - this.pageCount
				end = this.pageCount
			}

			if (start <= 0) {
				end += (start - 1) * -1
				start = 1
			}

			end = end > this.pageCount ? this.pageCount : end

			let len = end - start + 1
			let arr = new Array(len)

			for (let i = 0; i < len; i++) {
				arr[i] = start + i
			}

			return arr
		},
	},

	created() {
		EventBus.$on(Events.setPagination, this.onSetPagination)
		EventBus.$on(EventsSearch.filtersUpdated, this.onFiltersUpdatedEvent)

		UrlUpdater.registerEvent(Events.pageNumberUpdated, "pageNumber")
		UrlUpdater.registerEvent(Events.setPagination, "pageNumber")

		this.setInitialSelectedFilters()
	},

	methods: {
		onPageClick(pageNumber) {
			this.changePageNumber(pageNumber)
		},

		onPrevClick() {
			const target = Math.max(this.pageNumber - 1, 1)
			this.changePageNumber(target)
		},

		onNextClick() {
			const target = Math.min(this.pageNumber + 1, this.pageCount)
			this.changePageNumber(target)
		},

		onSetPagination(e) {
			if (e.group !== this.group) return

			this.pageNumber = e.data.pageNumber
			this.itemCount = e.data.itemCount
			this.pageSize = e.data.pageSize
		},

		changePageNumber(target) {
			if (target === this.pageNumber) return

			this.pageNumber = target

			const filtersToDisplay = this.filters.map((filter) => filter.name)

			const e = {
				data: {
					pageNumber: target,
					selectionCriteria: this.selectionCriteria,
					filtersToDisplay: filtersToDisplay,
					searchFilters: cloneObject(this.selectedSearchFilter),
					categories: cloneObject(this.selectedCategory),
					ranges: cloneObject(this.selectedRange),
					items: cloneObject(this.selectedItem),
					sizes: cloneObject(this.selectedSize),
				},
				group: this.group,
			}

			EventBus.$emit(Events.pageNumberUpdated, e)
		},

		iconHref(key) {
			if (iconMap[key]) return "#" + iconMap[key].id
			return null
		},

		onFiltersUpdatedEvent(e) {
			if (e.group !== this.group) {
				return
			}

			this.selectedSearchFilter = cloneObject(e.data.searchFilters)
			this.selectedCategory = cloneObject(e.data.categories)
			this.selectedRange = cloneObject(e.data.ranges)
			this.selectedItem = cloneObject(e.data.items)
			this.selectedSize = cloneObject(e.data.sizes)
		},

		setInitialSelectedFilters() {
			const selectedSearchFilters = []
			const selectedCategories = []
			const selectedRanges = []
			const selectedItems = []
			const selectedSizes = []

			this.filters.forEach((filter) => {
				if (filter.name === "searchFilter") {
					filter.options.forEach((option) => {
						if (option.selected) selectedSearchFilters.push(option.value)
					})
				} else if (filter.name === "category") {
					filter.options.forEach((option) => {
						if (option.selected) selectedCategories.push(option.value)
					})
				} else if (filter.name === "range") {
					filter.options.forEach((option) => {
						if (option.selected) selectedRanges.push(option.value)
					})
				} else if (filter.name === "item") {
					filter.options.forEach((option) => {
						if (option.selected) selectedItems.push(option.value)
					})
				} else if (filter.name === "size") {
					filter.options.forEach((option) => {
						if (option.selected) selectedSizes.push(option.value)
					})
				}
			})

			this.selectedSearchFilter = selectedSearchFilters
			this.selectedCategory = selectedCategories
			this.selectedRange = selectedRanges
			this.selectedItem = selectedItems
			this.selectedSize = selectedSizes
		},
	},
}
</script>
